<template>
  <el-row>
    <div class="d-flex align-items-center ml-auto">
      <div
        class="ml-auto pipeline-status-indicator"
        :class="PipelineStatus.Succeeded"
      />
      <h4 class="ml-1 pipeline-status-label">{{ $t('success') }}</h4>
      <div
        class="ml-3 pipeline-status-indicator"
        :class="PipelineStatus.InProgress"
      ></div>
      <h4 class="ml-1 pipeline-status-label">{{ $t('running') }}</h4>
      <div
        class="ml-3 pipeline-status-indicator"
        :class="PipelineStatus.Failed"
      ></div>
      <h4 class="ml-1 pipeline-status-label">{{ $t('failed') }}</h4>
    </div>
  </el-row>
  <el-row class="mt-2 mb-1">
    <el-col>
      <h4 v-if="loadingPipelineRuns && !filteredApiRuns.length">
        {{ $t('loading') }}
      </h4>
      <h4 v-else>{{ filteredApiRuns.length }} &#183; {{ $t('apiRuns') }}</h4>
    </el-col>
  </el-row>
  <el-row class="d-block">
    <base-table
      :show-loading-table="loadingPipelineRuns && !filteredApiRuns.length"
      :show-no-content-table="!filteredApiRuns.length"
      :error-message="$t('noApiRunsToDisplay')"
    >
      <api-runs-table
        :pipeline-runs="filteredApiRuns"
        :sort-params="sortParams"
        @update-sort-params="updateSortParams"
      />
    </base-table>
  </el-row>
</template>

<script lang="ts">
import { ElRow, ElCol } from 'element-plus';
import { useStore } from 'vuex';
import { computed, ComputedRef, onBeforeUnmount, onMounted } from 'vue';
import BaseTable from '@/components/tables/BaseTable.vue';
import ApiRunsTable from './components/ApiRunsTable.vue';
import { SortParams } from '@/services/web-api/dto/sortParams';
import { PipelineStatus } from '@/services/web-api/enums/pipelineStatus';
import { setEtpInterval, destroyEtpInterval } from '@/utils/interval';
import { PipelineRunDto } from '@etp/etp-pipelines-client/axios';

export default {
  name: 'GainApiRuns',
  components: {
    BaseTable,
    ApiRunsTable,
    ElRow,
    ElCol
  },
  setup() {
    const store = useStore();
    const refreshListPipelineRunKey = `import-GainApiRuns`;
    const refreshListPipelineRunsInMilliseconds = 30 * 1000;

    const loadingPipelineRuns: ComputedRef<boolean> = computed<boolean>(
      () => store.getters[`import/loadingListPipelineRuns`]
    );

    const pipelineRuns: ComputedRef<Array<PipelineRunDto>> = computed(
      () => store.getters[`import/pipelineRuns`]
    );

    const filteredApiRuns = computed(() => {
      return pipelineRuns.value.filter(
        run => run.pipelineName === 'etrade_report_fetcher'
      );
    });

    const sortParams: ComputedRef<SortParams> = computed(
      () => store.getters[`import/pipelineRunsSortParams`]
    );

    const listPipelineRuns = async () => {
      await store.dispatch(`import/listPipelineRuns`);
    };

    const updateSortParams = async (value: SortParams) => {
      store.commit(`import/SET_PIPELINE_RUNS_SORT_PARAMS`, value);
      await listPipelineRuns();
    };

    onMounted(async () => {
      await listPipelineRuns();
      setEtpInterval(
        refreshListPipelineRunKey,
        listPipelineRuns,
        refreshListPipelineRunsInMilliseconds
      );
    });

    onBeforeUnmount(() => {
      destroyEtpInterval(refreshListPipelineRunKey);
    });

    return {
      filteredApiRuns,
      loadingPipelineRuns,
      PipelineStatus,
      sortParams,
      updateSortParams
    };
  }
};
</script>

<style scoped lang="scss">
.pipeline-status-label {
  color: $etp-deloitte-blue-5;
  width: fit-content;
}
</style>
